import { useRef } from "react"
import { X } from "lucide-react"
import { formatDistanceToNow } from "date-fns"
import { useNavigate } from "@tanstack/react-router"
import { useVirtualizer } from "@tanstack/react-virtual"
import { toast } from "sonner"
import {
  useNotificationsInbox,
  useMarkRead,
  useDismiss,
  isSosEvent,
  type NotificationListItem,
} from "@/entities/notification"
import { Badge } from "@/shared/components/ui/badge"
import { Skeleton } from "@/shared/components/ui/skeleton"

type BadgeTone = "neutral" | "ok" | "warn" | "bad" | "info"

const MAX_UNREAD = 100

function priorityTone(priority: string): BadgeTone {
  const value = priority.toLowerCase()
  if (value === "critical") return "bad"
  if (value === "standard") return "info"
  return "neutral"
}

/** Events whose sourceId points at an incident, so the row can deep-link to it. */
function isIncidentLinked(event: string): boolean {
  return isSosEvent(event) || event.toLowerCase().includes("incident")
}

const EMPLOYEE_PROFILE_EVENTS: readonly string[] = ["NewEmployeeAwaitingReview", "PersonRecordsExportFinished"]

function employeeProfileUserId(item: NotificationListItem): string | null {
  if (!item.payload) return null
  const isEmploymentNotice = item.event.startsWith("Employment")
  if (!EMPLOYEE_PROFILE_EVENTS.includes(item.event) && !isEmploymentNotice) return null
  try {
    const payload = JSON.parse(item.payload) as { EmployeeUserId?: unknown; userId?: unknown }
    const userId = isEmploymentNotice ? payload.userId : payload.EmployeeUserId
    const text = typeof userId === "number" ? String(userId) : userId
    return typeof text === "string" && /^\d+$/.test(text) ? text : null
  } catch {
    return null
  }
}

type MemoExportTarget =
  | { scope: "Site"; clientId: string; siteId: string }
  | { scope: "Guard"; employeeId: string }

const isId = (value: unknown): value is string => typeof value === "string" && /^\d+$/.test(value)

function memoExportTarget(item: NotificationListItem): MemoExportTarget | null {
  if (item.event !== "MemoBookExportFinished" || !item.payload) return null
  try {
    const payload = JSON.parse(item.payload) as { Scope?: unknown; ScopeId?: unknown; ClientId?: unknown }
    if (!isId(payload.ScopeId)) return null
    if (payload.Scope === "Site" && isId(payload.ClientId)) {
      return { scope: "Site", clientId: payload.ClientId, siteId: payload.ScopeId }
    }
    if (payload.Scope === "Guard") return { scope: "Guard", employeeId: payload.ScopeId }
    return null
  } catch {
    return null
  }
}

type NotificationPanelProps = {
  onClose?: () => void
}

export function NotificationPanel({ onClose }: NotificationPanelProps) {
  const navigate = useNavigate()
  const { data, isLoading } = useNotificationsInbox(1, MAX_UNREAD, true)
  const markRead = useMarkRead()
  const dismiss = useDismiss()

  const parentRef = useRef<HTMLDivElement>(null)

  // Server returns unread; filter again so optimistic mark-read removes rows instantly.
  const items = (data?.items.items ?? []).filter((n) => n.readAt == null)

  const virtualizer = useVirtualizer({
    count: items.length,
    getScrollElement: () => parentRef.current,
    estimateSize: () => 88,
    overscan: 8,
  })

  const handleRowClick = (item: NotificationListItem) => {
    markRead.mutate(item.id, {
      onError: () => toast.error("Couldn't mark notification as read"),
    })
    if (item.sourceId != null && isIncidentLinked(item.event)) {
      navigate({
        to: "/incidents/$incidentId",
        params: { incidentId: String(item.sourceId) },
      })
      onClose?.()
      return
    }
    const memoTarget = memoExportTarget(item)
    if (memoTarget?.scope === "Site") {
      navigate({
        to: "/clients/$clientId/sites/$siteId/memo-book",
        params: { clientId: memoTarget.clientId, siteId: memoTarget.siteId },
      })
      onClose?.()
      return
    }
    if (memoTarget?.scope === "Guard") {
      navigate({
        to: "/employees/$employeeId/profile",
        params: { employeeId: memoTarget.employeeId },
        search: { tab: "memo-book" },
      })
      onClose?.()
      return
    }
    const employeeId = employeeProfileUserId(item)
    if (employeeId) {
      navigate({ to: "/employees/$employeeId/profile", params: { employeeId } })
      onClose?.()
    }
  }

  const handleDismiss = (e: React.MouseEvent, id: number) => {
    e.stopPropagation()
    dismiss.mutate(id, {
      onError: () => toast.error("Couldn't dismiss notification"),
    })
  }

  return (
    <div className="flex flex-col">
      <div className="flex items-center justify-between border-b border-line px-4 py-3">
        <span className="text-[13px] font-semibold text-ink">Notifications</span>
        {items.length > 0 && (
          <span className="text-[11px] text-ink-4">{items.length} unread</span>
        )}
      </div>

      {isLoading ? (
        <div className="flex flex-col gap-3 p-4">
          {Array.from({ length: 4 }).map((_, index) => (
            <div key={index} className="flex flex-col gap-2">
              <Skeleton className="h-3 w-3/4" />
              <Skeleton className="h-3 w-1/2" />
            </div>
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="px-4 py-10 text-center text-[13px] text-ink-3">
          You're all caught up
        </div>
      ) : (
        <div ref={parentRef} className="max-h-[440px] overflow-y-auto">
          <div
            style={{ height: virtualizer.getTotalSize(), width: "100%", position: "relative" }}
          >
            {virtualizer.getVirtualItems().map((virtualRow) => {
              const item = items[virtualRow.index]
              return (
                <div
                  key={item.id}
                  data-index={virtualRow.index}
                  ref={virtualizer.measureElement}
                  style={{
                    position: "absolute",
                    top: 0,
                    left: 0,
                    width: "100%",
                    transform: `translateY(${virtualRow.start}px)`,
                  }}
                >
                  <div
                    role="button"
                    tabIndex={0}
                    onClick={() => handleRowClick(item)}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" || e.key === " ") {
                        e.preventDefault()
                        handleRowClick(item)
                      }
                    }}
                    className="group flex cursor-pointer gap-2.5 border-b border-line px-4 py-3 transition-colors hover:bg-hover"
                  >
                    <span
                      aria-hidden
                      className="mt-1.5 h-2 w-2 shrink-0 rounded-full bg-brand-accent"
                    />
                    <div className="flex min-w-0 flex-1 flex-col gap-1">
                      <div className="flex items-center gap-2">
                        <Badge tone={priorityTone(item.priority)}>{item.priority}</Badge>
                        {isSosEvent(item.event) && (
                          <Badge tone="bad" className="text-[10px] font-bold uppercase tracking-wide">
                            SOS
                          </Badge>
                        )}
                        <span className="ml-auto shrink-0 text-[11px] text-ink-4">
                          {formatDistanceToNow(new Date(item.createdOn), { addSuffix: true })}
                        </span>
                      </div>
                      <span className="text-[13px] font-medium text-ink">{item.title}</span>
                      {item.body && (
                        <span className="line-clamp-2 text-[12px] text-ink-3">{item.body}</span>
                      )}
                    </div>
                    <button
                      type="button"
                      aria-label="Dismiss notification"
                      onClick={(e) => handleDismiss(e, item.id)}
                      className="flex h-6 w-6 shrink-0 items-center justify-center rounded-md text-ink-4 opacity-0 transition-opacity hover:bg-hover hover:text-ink-2 group-hover:opacity-100"
                    >
                      <X size={13} strokeWidth={1.8} />
                    </button>
                  </div>
                </div>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
